import type { RealProfile } from '../types/profiles';
import type { RankedItem, HourBlock } from '../types/communication';
import type { RoutineDetailRow, SubRoutineDetailRow } from '../types/routines';

export const FIXED_APP_ROUTINES_COUNT = 8;

interface WeekdayCount {
  label: string;
  count: number;
}

const WEEKDAY_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

function parseWeekdays(diasSemana: string): number[] {
  return diasSemana
    .split(',')
    .map((d) => parseInt(d.trim(), 10))
    .filter((d) => !Number.isNaN(d) && d >= 1 && d <= 7);
}

function weekdaysLabel(diasSemana: string): string {
  const days = parseWeekdays(diasSemana);
  if (days.length === 0) return '—';
  if (days.length === 7) return 'Todos os dias';
  return days.map((d) => WEEKDAY_LABELS[d - 1]).join(', ');
}

const HOUR_BLOCK_RANGES: [number, number][] = [
  [0, 2], [2, 4], [4, 6], [6, 8], [8, 10], [10, 12],
  [12, 14], [14, 16], [16, 18], [18, 20], [20, 22], [22, 24],
];

function hourFromString(hora: string): number | null {
  const match = /^(\d{1,2}):/.exec(hora);
  if (!match) return null;
  return parseInt(match[1], 10);
}

export interface RealRoutinesAnalytics {
  routinesUsed: number;
  rankedRoutines: RankedItem[];
  routineDetails: RoutineDetailRow[];
  subRoutineDetails: SubRoutineDetailRow[];
  byWeekday: WeekdayCount[];
  byHourBlock: HourBlock[];
  weekdayByRoutine: Record<string, string | number>[];
  routineSeries: { key: string; label: string; color: string }[];
  alarmAdherence: { routineName: string; alarmCount: number }[];
  routinesByOnceAWeekAlarms: RankedItem[];
  routinesByEverydayAlarms: RankedItem[];
}

const SERIES_COLORS = ['#4F7CFF', '#22C55E', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16', '#EF4444'];

export function computeRealRoutinesAnalytics(profiles: RealProfile[]): RealRoutinesAnalytics {
  const routineNameById = new Map<string, string>();
  for (const profile of profiles) {
    for (const r of profile.routines) routineNameById.set(r.routineId, r.name);
  }

  const subRoutinesByRoutine = new Map<string, SubRoutineDetailRow[]>();
  const allSubRoutines: { routineName: string; hora: string; alarme: boolean; diasSemana: string; titulo: string }[] = [];

  for (const profile of profiles) {
    for (const sr of profile.subRoutines) {
      const routineName = routineNameById.get(sr.routineId) ?? sr.routineId;
      const row: SubRoutineDetailRow = {
        name: sr.titulo || routineName,
        routineName,
        hour: sr.hora || '—',
        hasAlarm: sr.alarme,
        weekdays: weekdaysLabel(sr.diasSemana),
      };
      const bucket = subRoutinesByRoutine.get(routineName) ?? [];
      bucket.push(row);
      subRoutinesByRoutine.set(routineName, bucket);
      allSubRoutines.push({ routineName, hora: sr.hora, alarme: sr.alarme, diasSemana: sr.diasSemana, titulo: sr.titulo });
    }
  }

  const rankedRoutines: RankedItem[] = [...subRoutinesByRoutine.entries()]
    .map(([name, rows]) => ({ name, value: rows.length }))
    .sort((a, b) => b.value - a.value);

  const routineDetails: RoutineDetailRow[] = [...subRoutinesByRoutine.entries()].map(([name, rows]) => {
    const hourCounts = new Map<string, number>();
    for (const r of rows) hourCounts.set(r.hour, (hourCounts.get(r.hour) ?? 0) + 1);
    const typicalHour = [...hourCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? '—';
    return {
      name,
      typicalHour,
      hasAlarm: rows.some((r) => r.hasAlarm),
      weekdays: [...new Set(rows.map((r) => r.weekdays))].join(' · '),
    };
  });

  const subRoutineDetails = allSubRoutines.map((sr) => ({
    name: sr.titulo || sr.routineName,
    routineName: sr.routineName,
    hour: sr.hora || '—',
    hasAlarm: sr.alarme,
    weekdays: weekdaysLabel(sr.diasSemana),
  }));

  const weekdayCounts = Array.from({ length: 7 }, () => 0);
  const weekdayByRoutineMap = new Map<number, Map<string, number>>();
  const routineNames = [...subRoutinesByRoutine.keys()];

  for (const sr of allSubRoutines) {
    for (const day of parseWeekdays(sr.diasSemana)) {
      weekdayCounts[day - 1] += 1;
      const routineMap = weekdayByRoutineMap.get(day) ?? new Map<string, number>();
      routineMap.set(sr.routineName, (routineMap.get(sr.routineName) ?? 0) + 1);
      weekdayByRoutineMap.set(day, routineMap);
    }
  }

  const byWeekday: WeekdayCount[] = WEEKDAY_LABELS.map((label, idx) => ({
    label,
    count: weekdayCounts[idx],
  }));

  const hourCounts = HOUR_BLOCK_RANGES.map(() => 0);
  for (const sr of allSubRoutines) {
    const hour = hourFromString(sr.hora);
    if (hour === null) continue;
    const blockIndex = HOUR_BLOCK_RANGES.findIndex(([start, end]) => hour >= start && hour < end);
    if (blockIndex >= 0) hourCounts[blockIndex] += 1;
  }
  const maxHour = Math.max(1, ...hourCounts);
  const byHourBlock: HourBlock[] = HOUR_BLOCK_RANGES.map(([start, end], idx) => ({
    label: `${String(start).padStart(2, '0')}h–${String(end).padStart(2, '0')}h`,
    count: hourCounts[idx],
    intensity: hourCounts[idx] / maxHour,
  }));

  const routineSeries = routineNames.slice(0, 8).map((name, idx) => ({
    key: name,
    label: name,
    color: SERIES_COLORS[idx % SERIES_COLORS.length],
  }));

  const weekdayByRoutine = WEEKDAY_LABELS.map((label, idx) => {
    const day = idx + 1;
    const routineMap = weekdayByRoutineMap.get(day);
    const row: Record<string, string | number> = { day: label };
    for (const name of routineNames) row[name] = routineMap?.get(name) ?? 0;
    return row;
  });

  const alarmCountByRoutine = new Map<string, number>();
  for (const sr of allSubRoutines) {
    if (!sr.alarme) continue;
    alarmCountByRoutine.set(sr.routineName, (alarmCountByRoutine.get(sr.routineName) ?? 0) + 1);
  }
  const alarmAdherence = [...alarmCountByRoutine.entries()]
    .map(([routineName, alarmCount]) => ({ routineName, alarmCount }))
    .sort((a, b) => b.alarmCount - a.alarmCount);

  const onceAWeekCounts = new Map<string, number>();
  const everydayCounts = new Map<string, number>();
  for (const sr of allSubRoutines) {
    if (!sr.alarme) continue;
    const days = parseWeekdays(sr.diasSemana);
    if (days.length === 1) onceAWeekCounts.set(sr.routineName, (onceAWeekCounts.get(sr.routineName) ?? 0) + 1);
    if (days.length === 7) everydayCounts.set(sr.routineName, (everydayCounts.get(sr.routineName) ?? 0) + 1);
  }

  const toRanked = (map: Map<string, number>): RankedItem[] =>
    [...map.entries()].map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

  return {
    routinesUsed: subRoutinesByRoutine.size,
    rankedRoutines,
    routineDetails,
    subRoutineDetails,
    byWeekday,
    byHourBlock,
    weekdayByRoutine,
    routineSeries,
    alarmAdherence,
    routinesByOnceAWeekAlarms: toRanked(onceAWeekCounts),
    routinesByEverydayAlarms: toRanked(everydayCounts),
  };
}
